// Chat Input for Club Penguin Game
// A fixed bottom input bar. Owns its keyboard shortcuts: 'T' opens it,
// Enter sends, Escape closes. Other components can check isFocused()
// before handling their own hotkeys.

const MAX_MESSAGE_LENGTH = 200;

export class ChatInput {
  private bar: HTMLElement;
  private input: HTMLInputElement;
  private onSend: (message: string) => void;
  private keydownHandler: (e: KeyboardEvent) => void;

  constructor(onSend: (message: string) => void) {
    this.onSend = onSend;

    this.bar = document.createElement('div');
    this.bar.className = 'chat-bar';

    this.input = document.createElement('input');
    this.input.type = 'text';
    this.input.placeholder = 'Type your message...';
    this.input.maxLength = MAX_MESSAGE_LENGTH;
    this.bar.appendChild(this.input);
    document.body.appendChild(this.bar);

    this.input.addEventListener('keydown', e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.send();
      }
      e.stopPropagation();
    });

    this.keydownHandler = e => {
      if (this.isFocused()) {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.close();
        }
        return;
      }
      if (e.key.toLowerCase() === 't' && !isTypingTarget(e.target)) {
        e.preventDefault();
        this.open();
      } else if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    };
    document.addEventListener('keydown', this.keydownHandler);
  }

  public isOpen(): boolean {
    return this.bar.classList.contains('open');
  }

  public isFocused(): boolean {
    return document.activeElement === this.input;
  }

  public open(): void {
    this.bar.classList.add('open');
    this.input.focus();
  }

  public close(): void {
    this.bar.classList.remove('open');
    this.input.value = '';
    this.input.blur();
  }

  public destroy(): void {
    document.removeEventListener('keydown', this.keydownHandler);
    this.bar.remove();
  }

  private send(): void {
    const message = this.input.value.trim();
    if (message) {
      this.onSend(message);
    }
    this.close();
  }
}

function isTypingTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
  );
}
